/**
 * Realtime Conversation Slice
 * Manages Azure OpenAI Realtime API conversation state
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export interface ConversationUsage {
  total_tokens?: number;
  input_tokens?: number;
  output_tokens?: number;
}

export interface PronunciationScore {
  turnIndex: number;
  accuracyScore: number;
  fluencyScore: number;
  completenessScore: number;
  pronScore: number;
  timestamp: number;
}

export interface RealtimeConversationState {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Recording state
  isRecording: boolean;
  isSpeaking: boolean; // AI is speaking
  isWaitingResponse: boolean;

  // Conversation data
  conversationId: string | null;
  sectionId: string | null;
  transcript: ConversationMessage[];
  currentTurn: number;
  maxTurns: number;

  // Usage tracking
  usage: ConversationUsage;

  // Pronunciation scores (parallel assessment)
  pronunciationScores: PronunciationScore[];

  // Assessment tracking
  assessmentId: string | null;
  isSubmittingAssessment: boolean;
  assessmentError: string | null;
}

const initialState: RealtimeConversationState = {
  isConnected: false,
  isConnecting: false,
  connectionError: null,

  isRecording: false,
  isSpeaking: false,
  isWaitingResponse: false,

  conversationId: null,
  sectionId: null,
  transcript: [],
  currentTurn: 0,
  maxTurns: 0,

  usage: {},

  pronunciationScores: [],

  assessmentId: null,
  isSubmittingAssessment: false,
  assessmentError: null,
};

const realtimeConversationSlice = createSlice({
  name: "realtimeConversation",
  initialState,
  reducers: {
    // Connection actions
    setConnecting(state, action: PayloadAction<boolean>) {
      state.isConnecting = action.payload;
      if (action.payload) {
        state.connectionError = null;
      }
    },
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
      state.isConnecting = false;
      if (!action.payload) {
        // Disconnected
        state.isRecording = false;
        state.isSpeaking = false;
        state.isWaitingResponse = false;
      }
    },
    setConnectionError(state, action: PayloadAction<string>) {
      state.connectionError = action.payload;
      state.isConnecting = false;
      state.isConnected = false;
    },

    // Recording state
    setRecording(state, action: PayloadAction<boolean>) {
      state.isRecording = action.payload;
    },
    setSpeaking(state, action: PayloadAction<boolean>) {
      state.isSpeaking = action.payload;
    },
    setWaitingResponse(state, action: PayloadAction<boolean>) {
      state.isWaitingResponse = action.payload;
    },

    // Conversation initialization
    initConversation(
      state,
      action: PayloadAction<{
        conversationId: string;
        sectionId: string;
        maxTurns: number;
      }>
    ) {
      state.conversationId = action.payload.conversationId;
      state.sectionId = action.payload.sectionId;
      state.maxTurns = action.payload.maxTurns;
      state.currentTurn = 0;
      state.transcript = [];
      state.usage = {};
      state.pronunciationScores = [];
      state.assessmentId = null;
      state.assessmentError = null;
    },

    // Transcript management
    addMessage(state, action: PayloadAction<ConversationMessage>) {
      state.transcript.push(action.payload);

      // Increment turn count for user messages
      if (action.payload.role === "user") {
        state.currentTurn += 1;
      }
    },
    updateLastMessage(
      state,
      action: PayloadAction<Partial<ConversationMessage>>
    ) {
      if (state.transcript.length > 0) {
        const lastIndex = state.transcript.length - 1;
        state.transcript[lastIndex] = {
          ...state.transcript[lastIndex],
          ...action.payload,
        };
      }
    },

    // Usage tracking
    updateUsage(state, action: PayloadAction<ConversationUsage>) {
      state.usage = {
        ...state.usage,
        ...action.payload,
      };
    },

    // Pronunciation scores
    addPronunciationScore(state, action: PayloadAction<PronunciationScore>) {
      state.pronunciationScores.push(action.payload);
    },

    // Assessment tracking
    setAssessmentId(state, action: PayloadAction<string>) {
      state.assessmentId = action.payload;
    },
    setSubmittingAssessment(state, action: PayloadAction<boolean>) {
      state.isSubmittingAssessment = action.payload;
      if (action.payload) {
        state.assessmentError = null;
      }
    },
    setAssessmentError(state, action: PayloadAction<string>) {
      state.assessmentError = action.payload;
      state.isSubmittingAssessment = false;
    },

    // Reset conversation
    resetConversation(state) {
      return initialState;
    },
  },
});

export const {
  setConnecting,
  setConnected,
  setConnectionError,
  setRecording,
  setSpeaking,
  setWaitingResponse,
  initConversation,
  addMessage,
  updateLastMessage,
  updateUsage,
  addPronunciationScore,
  setAssessmentId,
  setSubmittingAssessment,
  setAssessmentError,
  resetConversation,
} = realtimeConversationSlice.actions;

export default realtimeConversationSlice.reducer;
